
import React, { useEffect } from 'react';
import 'common/polyfill';
import 'sanitize.css';
import 'common/styles/global.css';
import { ThemeProvider } from '@emotion/react';
import 'video.js/dist/video-js.css';
import '../common/Components/Player/styles/index.css';
import theme from 'common/theme';
import { LanguageProvider, __t } from 'common/i18n';
import { CacheProvider } from 'common/CacheProvider';
import Head from 'next/head';
import { Main } from 'common/styles';
import RouteChangeLoading from 'common/RouteChangeLoading';
import { SWRConfig } from 'swr';
import { AlertServiceContainer } from 'common/AlertService';
import TagManager from 'react-gtm-module';
import Script from 'next/script';
import useScrollRestore from 'common/useScrollRestore';
import getConfig from 'next/config';
import SystemMessage from 'common/Header/SystemMessage';
import TvNoticeBanner from '../common/Footer/TvNotice';
import { useRouter } from 'next/router';
import { persistIOSPWARoute, restoreIOSPWARoute } from '../common/utils/pwaRestore';
import { safeJsonLd } from '../common/utils/safeJsonLd';
import { DEFAULT_SITE_DESCRIPTION, SITE_URL } from 'common/Seo';
import '../public/fonts/dana.css';
import '../public/fonts/roboto.css';

// import { RouterScrollProvider } from '@moxy/next-router-scroll';

const { publicRuntimeConfig } = getConfig();

const swrConfig = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  refreshInterval: 1800000,
  dedupingInterval: 300000,
  focusThrottleInterval: 600000,
};

const siteNavigationItems = [
  {
    name: __t('watch.movies.series'),
    url: `${SITE_URL}/`,
  },
  {
    name: __t('common.search'),
    url: `${SITE_URL}/contents`,
  },
  {
    name: __t('common.categories'),
    url: `${SITE_URL}/categories`,
  },
  {
    name: __t('common.filmnet_exclusive'),
    url: `${SITE_URL}/contents?categories=b0bb89f6-8960-420f-b439-b687d8246af8`,
  },
  {
    name: __t('static.download.apps.title'),
    url: `${SITE_URL}/apps`,
  },
  {
    name: __t('common.filmnet_ads'),
    url: `${SITE_URL}/ads`,
  },
];

const orgSchemaObj = {
  '@context': 'https://schema.org',
  '@graph': [{
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    url: `${SITE_URL}/`,
    logo: {
      '@type': 'ImageObject',
      '@id': `${SITE_URL}/#logo`,
      url: `${SITE_URL}/android-chrome-512x512.png`,
      contentUrl: `${SITE_URL}/android-chrome-512x512.png`,
      width: 512,
      height: 512,
      caption: __t('static_siteName'),
    },
    sameAs: ['https://www.instagram.com/filmnet.ir_', 'https://t.me/filmnetofficial', 'https://x.com/filmnetofficial', 'https://www.linkedin.com/company/filmnetir/'],
    name: __t('static_siteName'),
    alternateName: 'Filmnet',
    description: DEFAULT_SITE_DESCRIPTION,
    email: 'contact@filmnet.ir',
    telephone: '+982158172000',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'South Abbas Shirazi Street, Molla Sadra Street',
      addressLocality: 'Tehran',
      addressCountry: 'IR',
      addressRegion: 'Tehran Province',
      postalCode: '1435884381',
    },
  }, {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: `${SITE_URL}/`,
    name: __t('static_siteName'),
    alternateName: 'Filmnet',
    publisher: {
      '@id': `${SITE_URL}/#organization`,
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/contents?query={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  }],
};

const siteNavigationSchemaObj = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Filmnet main site navigation',
  itemListElement: siteNavigationItems.map((item, index) => ({
    '@type': 'SiteNavigationElement',
    position: index + 1,
    name: item.name,
    url: item.url,
  })),
};

export default function App({ Component, pageProps }) {
  const router = useRouter();

  useEffect(() => {
    restoreIOSPWARoute(router.replace);
    const gtmId = publicRuntimeConfig.NEXT_PUBLIC_GTM_ID;
    if (gtmId) TagManager.initialize({ gtmId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      persistIOSPWARoute(url);
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useScrollRestore();
  return (
    <>
      <SWRConfig value={swrConfig}>
        <LanguageProvider>
          <ThemeProvider theme={theme}>
            <CacheProvider>
              <Head>
                <title>{__t('static_siteName')}</title>

                <meta name="description" content={DEFAULT_SITE_DESCRIPTION} key="description" />
                <meta name="application-name" content={__t('static_siteName')} />
                {/* Allow pinch-zoom for a11y (low vision). Avoid maximum-scale/user-scalable=no; */}
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <meta httpEquiv="X-UA-Compatible" content="ie=edge" />
                <meta name="msapplication-TileColor" content="#1c1c1c" />
                <meta name="theme-color" content="#ffffff" />
                <meta name="mobile-web-app-capable" content="yes" />
                <meta name="apple-mobile-web-app-capable" content="yes" />
                <meta name="apple-mobile-web-app-title" content={`${__t('static_siteName')} PWA`} />
                <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
                <meta
                  name="google-site-verification"
                  content="_4JW2LTRq3nRuRQ1u48V1FIbZnrryjVH2C7g5jJ0fgc"
                />

                {/* Open Graph (very important for sharing & CTR)*/}
                <meta property="og:type" content="website" key="og:type" />
                <meta property="og:locale" content="fa_IR" key="og:locale" />
                <meta property="og:url" content={SITE_URL} key="og:url" />
                <meta property="og:site_name" content={__t('static_siteName')} key="og:site_name" />
                <meta property="og:title" content={__t('static_siteName')} key="og:title" />
                <meta property="og:description" content={DEFAULT_SITE_DESCRIPTION} key="og:description" />

                {/* Twitter*/}
                <meta name="twitter:card" content="summary" key="twitter:card" />
                <meta name="twitter:site" content="@filmnetofficial" key="twitter:site" />
                <meta name="twitter:title" content={__t('static_siteName')} key="twitter:title" />
                <meta name="twitter:description" content={DEFAULT_SITE_DESCRIPTION} key="twitter:description" />

                {/* DC*/}
                <meta name="DC.Language" content="fa" key="DC.Language" />
                <meta name="DC.Title" content={__t('static_siteName')} key="DC.Title" />
                <meta name="DC.Description" content={DEFAULT_SITE_DESCRIPTION} key="DC.Description" />

                <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
                <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
                <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
                <link rel="manifest" href="/manifest.json" />
                <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#aa0a00" />
                <link rel="shortcut icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
                <link rel="shortcut icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />

                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{
                    __html: safeJsonLd(orgSchemaObj),
                  }}
                >
                </script>

                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{
                    __html: safeJsonLd(siteNavigationSchemaObj),
                  }}
                >
                </script>
              </Head>
              <RouteChangeLoading />
              <SystemMessage />
              <Main>
                <AlertServiceContainer />
                {/* <RouterScrollProvider disableNextLinkScroll={false}> */}
                <Component {...pageProps} />
                {/* </RouterScrollProvider> */}
              </Main>
              <TvNoticeBanner />
            </CacheProvider>
          </ThemeProvider>
        </LanguageProvider>
      </SWRConfig>
      <Script type="text/javascript" src="/static/goftino/index.js" strategy="lazyOnload" />
    </>
  );
}
